import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

// Admin endpoints are proxied through nginx at /admin/
// In production: https://chatai.comturk.com/admin/
// In local dev: http://localhost:3000/admin/ (or http://localhost:8002/admin/ direct)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SystemHealthPage = () => {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [healthData, setHealthData] = useState(null);

  const fetchHealthStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/admin/crawl_status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('[SystemHealth] API response:', response.data);
      setHealthData(response.data);
    } catch (err) {
      console.error('Error fetching health status:', err);
      setError(err.response?.data?.detail || 'Failed to fetch system health status');
    } finally {
      setLoading(false);
    }
  };

  const fixMissingEmbeddings = async () => {
    setFixing(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/admin/fix_embeddings`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      setSuccess(response.data.message);
      // Refresh health status after fix
      await fetchHealthStatus();
    } catch (err) {
      console.error('Error fixing embeddings:', err);
      setError(err.response?.data?.detail || 'Failed to fix embeddings');
    } finally {
      setFixing(false);
    }
  };

  useEffect(() => {
    fetchHealthStatus();
  }, []);

  const getStatusColor = (status) => {
    if (status === 'healthy') return 'success';
    if (status === 'attention_needed') return 'warning';
    return 'error';
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy') return <CheckCircleIcon color="success" />;
    return <WarningIcon color="warning" />;
  };

  // Check if healthData is valid
  if (!healthData || !healthData.health) {
    if (loading) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }
    if (error) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      );
    }
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          System Health
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchHealthStatus}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2}} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && !healthData ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : healthData ? (
        <>
          {/* Overall System Status */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              {getStatusIcon(healthData.health.system_status)}
              <Typography variant="h6" ml={1}>
                System Status: 
                <Chip 
                  label={healthData.health.system_status.replace('_', ' ').toUpperCase()} 
                  color={getStatusColor(healthData.health.system_status)}
                  sx={{ ml: 1 }}
                />
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Last updated: {new Date(healthData.timestamp).toLocaleString()}
            </Typography>
          </Paper>

          {/* Statistics Grid */}
          <Grid container spacing={3} mb={3}>
            {/* User Files */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Files
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {healthData.user_files.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Files
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2">
                    Completed: {healthData.user_files.status_counts.completed || 0}
                  </Typography>
                  <Typography variant="body2">
                    Failed: {healthData.user_files.status_counts.failed || 0}
                  </Typography>
                  <Typography variant="body2">
                    Processing: {healthData.user_files.status_counts.embedding || 0}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    Completion Rate: {healthData.user_files.completion_rate}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Document Chunks */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Document Chunks
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {healthData.document_chunks.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Chunks
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2">
                    Completed: {healthData.document_chunks.status_counts.completed || 0}
                  </Typography>
                  <Typography variant="body2">
                    Unique Hashes: {healthData.document_chunks.unique_hashes || 0}
                  </Typography>
                  <Typography variant="body2">
                    Duplicates: {healthData.document_chunks.duplicates || 0}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2">
                    Failed: {healthData.document_chunks.status_counts.failed || 0}
                  </Typography>
                  <Typography variant="body2">
                    Pending: {healthData.document_chunks.status_counts.pending || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Milvus Embeddings */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Milvus Embeddings
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {healthData.milvus.total_entities || healthData.milvus.total_embeddings || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Vector Embeddings (Total Entities)
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2">
                    Unique Hashes: {healthData.milvus.unique_hashes || 0}
                  </Typography>
                  <Typography variant="body2">
                    Duplicates: {healthData.milvus.duplicates || 0}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  {Math.abs(healthData.health.embedding_gap || 0) > 10 ? (
                    <>
                      <Typography variant="body2" color="warning.main">
                        ⚠️ Gap: {healthData.health.embedding_gap} unique hashes
                      </Typography>
                      <Typography variant="caption" display="block" mt={1}>
                        {healthData.health.note || 'Difference between DB and Milvus unique hashes'}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="success.main">
                      ✓ System is healthy (gap: {healthData.health.embedding_gap || 0})
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Fix Action */}
          {healthData.health.embedding_gap > 10 && (
            <Paper sx={{ p: 3, bgcolor: 'warning.light' }}>
              <Typography variant="h6" gutterBottom>
                Action Required
              </Typography>
              <Typography variant="body1" mb={2}>
                Found {healthData.health.embedding_gap} unique chunk_hashes in the database that are not present in Milvus. 
                This means some chunks won't appear in search results.
              </Typography>
              <Typography variant="body2" mb={2} color="text.secondary">
                Note: The gap is calculated using unique chunk_hashes, not total chunks. Some duplicate chunks in the database are normal.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                startIcon={fixing ? <CircularProgress size={20} /> : <BuildIcon />}
                onClick={fixMissingEmbeddings}
                disabled={fixing}
              >
                {fixing ? 'Fixing...' : 'Fix Missing Embeddings'}
              </Button>
              <Typography variant="caption" display="block" mt={1}>
                This will reset missing chunks to 'pending' status. The embedding processor will automatically
                process them in the background (~40 seconds per 32 chunks).
              </Typography>
            </Paper>
          )}
        </>
      ) : null}
    </Container>
  );
};

export default SystemHealthPage;

